// Documents per activity on a project page, the same for both sides
// (Non-Penyedia PBJ project, Penyedia paket project; API /activity-docs):
//
//   order     a note under the stage stepper: drafting in order is advised
//             (each saved document is the next draft's reference), never
//             required; every activity stays open
//   buttons   every activity (and stage template) gets Buat dokumen and
//             Buat dengan arahan (a dialog for the user's own instructions), plus
//             a line with its saved version or open draft that reopens it;
//             a PBJ row also gets Unggah dokumen (ActivityDocUpload): a
//             Word, PDF or Excel file as the next version, the panel opening
//             on it afterwards
//   warning   drafting while earlier activities are neither ticked nor saved
//             asks first (the API answers 409 skipped_activities); Tetap buat
//             dokumen drafts anyway
//   panel     the draft never leaves the page: it opens in the project's
//             right panel (in a dialog below a 960px project frame, where the
//             panel sits under the page), with Simpan ke database (the
//             activity's next version, the next activity's reference, its
//             Word file kept in the Drive; Simpan file ke Drive retries a
//             copy that failed), Unduh
//             .docx, Unggah hasil edit (an edited Word, PDF or Excel file as the next version)
//             and Buat ulang
//   reviu     Reviu dokumen (a row with a saved version, and the panel of a
//             saved version): Pluto reviews the saved version on request,
//             never automatically; the panel's Hasil reviu tab shows the
//             status, the summary and the findings per kategori, each with
//             Perbaiki dengan arahan (the Buat dengan arahan dialog, prefilled
//             with its saran); the row shows the latest review's status
//
// One provider per project page holds the state; the pieces read it through
// useActivityDocs(), which is null outside a provider.
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import * as API from "../api";
import type { ActivityDoc, ActivityDocReview, ActivityDocState, ReviewFinding, ReviewStatus, SkippedActivity } from "../api";
import { DocumentBlocks } from "../chat/DocumentCard";
import { formatDateTime } from "../lib/format";
import { saveFile } from "../pbj/docx";
import { useFeatures, type FeatureState } from "../shell/features";
import { Badge, Dialog, EmptyState, Field, Notice, Tabs } from "../ui/components";
import { GenerationError, GenerationProgress, useElapsed } from "../ui/generation";
import { Glyph } from "../ui/icons";
import { useSidePanel } from "./SidePanel";

/** Instructions per draft (the API's MAX_INSTRUCTIONS). */
export const MAX_INSTRUCTIONS = 2_000;
/** Characters of one section in the editor (the API's MAX_SECTION_TEXT). */
export const MAX_SECTION_TEXT = 20_000;
/** An uploaded document (the API's MAX_UPLOAD_BYTES). */
export const DOC_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
/** The file picker's filter for an uploaded document: Word, PDF or Excel. */
export const DOC_UPLOAD_ACCEPT =
  ".docx,.pdf,.xlsx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** The problem with a document to upload, named for the person, or null when it may be sent (the API checks again by content). */
export function checkDocUpload(file: File): string | null {
  const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".") + 1).toLowerCase() : "";
  if (!["docx", "pdf", "xlsx"].includes(ext)) return `${file.name} belum didukung. Pilih file Word (.docx), PDF, atau Excel (.xlsx).`;
  if (file.size > DOC_UPLOAD_MAX_BYTES) return `${file.name} melebihi batas 10 MB.`;
  if (file.size === 0) return `${file.name} kosong.`;
  return null;
}
/** Below this project frame width the right panel sits under the page. */
const WIDE_FRAME = 960;

/** Drafting is one Pluto turn of the template library: projects, templates, the chat and document generation. */
export function canActivityDocs(features: FeatureState): boolean {
  return features.list !== null && features.has("projects") && features.has("templates") && features.has("chat") && features.has("document_generation");
}

/** The template key of a stage template. */
export function templateKey(templateId: string): string {
  return `tpl:${templateId}`;
}

/** The advised drafting order, as the note under the stage stepper says it. */
export const SEQUENCE_TITLE = "Disarankan membuat dokumen secara berurutan";
export const SEQUENCE_TEXT =
  "Mulailah dari tahap dan aktivitas paling awal. Setiap dokumen yang sudah disimpan menjadi acuan draf berikutnya, sehingga hasilnya lebih akurat. Urutan ini opsional: dokumen aktivitas mana pun tetap dapat dibuat.";

/** The note on the advised drafting order; none where drafting is off. */
export function SequenceNote() {
  if (!canActivityDocs(useFeatures())) return null;
  return (
    <Notice tone="info" title={SEQUENCE_TITLE}>
      {SEQUENCE_TEXT}
    </Notice>
  );
}

interface Target {
  key: string;
  /** The document's name, as the row shows it. */
  title: string;
}

type PanelTab = "doc" | "review";

type PanelState =
  | { kind: "generating"; target: Target }
  | { kind: "loading"; target: Target; tab?: PanelTab }
  | { kind: "error"; target: Target; error: unknown; retry: () => void }
  | { kind: "doc"; target: Target; doc: ActivityDoc; notice: string | null; noticeTitle?: string; tab?: PanelTab };

/** One document's review in the panel: loaded from the API, being made, or failed. */
export interface ReviewSlot {
  loaded: boolean;
  review: ActivityDocReview | null;
  running: boolean;
  error: unknown;
}

const EMPTY_SLOT: ReviewSlot = { loaded: false, review: null, running: false, error: null };

/** The review status as the badge says it, and its tone. */
export const REVIEW_STATUS_LABEL: Record<ReviewStatus, string> = { siap: "Siap", perlu_perbaikan: "Perlu perbaikan", belum_lengkap: "Belum lengkap" };
const REVIEW_STATUS_TONE: Record<ReviewStatus, "success" | "warning" | "danger"> = { siap: "success", perlu_perbaikan: "warning", belum_lengkap: "danger" };
/** The kategori in the order the results list them. */
export const REVIEW_CATEGORIES: { id: ReviewFinding["kategori"]; label: string }[] = [
  { id: "kelengkapan", label: "Kelengkapan" },
  { id: "konsistensi", label: "Konsistensi dengan dokumen sebelumnya" },
  { id: "aturan", label: "Kesesuaian aturan" },
  { id: "lainnya", label: "Lainnya" },
];
const LEVEL_LABEL: Record<ReviewFinding["tingkat"], string> = { tinggi: "Tinggi", sedang: "Sedang", rendah: "Rendah" };
const LEVEL_TONE: Record<ReviewFinding["tingkat"], "danger" | "warning" | "neutral"> = { tinggi: "danger", sedang: "warning", rendah: "neutral" };
const LEVEL_ORDER: ReviewFinding["tingkat"][] = ["tinggi", "sedang", "rendah"];

/** The instruction Perbaiki dengan arahan starts the Buat dengan arahan dialog with. */
export function fixInstruction(f: ReviewFinding): string {
  const where = f.bagian ? `Perbaiki bagian "${f.bagian}"` : "Perbaiki dokumen";
  const what = f.saran || f.temuan;
  return `${where}: ${what}${f.saran ? `\nTemuan reviu: ${f.temuan}` : ""}`.slice(0, MAX_INSTRUCTIONS);
}

export interface ActivityDocsApi {
  projectId: string;
  state(key: string): ActivityDocState | undefined;
  /** The key being drafted, one at a time. */
  busy: string | null;
  /** The saved version being reviewed, one at a time. */
  reviewing: string | null;
  generate(target: Target, instructions?: string): void;
  askInstructions(target: Target): void;
  open(target: Target, docId: string): void;
  /** Unggah dokumen: a file as the activity's next saved version, straight from its row. */
  upload(target: Target, file: File): Promise<ActivityDoc>;
  /** Reviu dokumen: opens the saved version's Hasil reviu in the panel and reviews it. */
  review(target: Target, docId: string): void;
  /** Opens the saved version's latest Hasil reviu in the panel, without a new review. */
  showReview(target: Target, docId: string): void;
}

const Ctx = createContext<ActivityDocsApi | null>(null);

export function useActivityDocs(): ActivityDocsApi | null {
  return useContext(Ctx);
}

/** True while the project frame is wide enough for the panel beside the page. */
function useWideFrame(probe: React.RefObject<HTMLElement | null>): boolean {
  const [wide, setWide] = useState(true);
  useEffect(() => {
    // The probe is the page frame's sibling (the provider wraps the frame).
    const frame = probe.current?.parentElement?.querySelector<HTMLElement>(".pj");
    if (!frame) return;
    // offsetWidth is in CSS px, like WIDE_FRAME; the bounding box shrinks with the root zoom (density.css).
    const measure = () => setWide(frame.offsetWidth >= WIDE_FRAME);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(frame);
    return () => ro.disconnect();
  }, [probe]);
  return wide;
}

export function ActivityDocsProvider({ projectId, children }: { projectId: string; children: ReactNode }) {
  const [states, setStates] = useState<Map<string, ActivityDocState>>(new Map());
  const [panel, setPanel] = useState<PanelState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [asking, setAsking] = useState<{ target: Target; prefill?: string } | null>(null);
  const [reviews, setReviews] = useState<Map<string, ReviewSlot>>(new Map());
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [skip, setSkip] = useState<{ target: Target; instructions?: string; skipped: SkippedActivity[]; total: number } | null>(null);
  const [, setPanelOpen] = useSidePanel();
  const probe = useRef<HTMLSpanElement>(null);
  const wide = useWideFrame(probe);

  const reload = useCallback(async () => {
    try {
      const items = await API.listActivityDocs(projectId);
      setStates(new Map(items.map((s) => [s.key, s])));
    } catch {
      // The buttons work without the status line.
    }
  }, [projectId]);

  useEffect(() => {
    setStates(new Map());
    setPanel(null);
    setReviews(new Map());
    reviewRequested.current.clear();
    void reload();
  }, [reload]);

  const patchReview = useCallback((docId: string, patch: Partial<ReviewSlot>) => {
    setReviews((prev) => new Map(prev).set(docId, { ...(prev.get(docId) ?? EMPTY_SLOT), ...patch }));
  }, []);

  /** The latest review of a saved version, read once per page. */
  const reviewRequested = useRef(new Set<string>());
  const loadReview = useCallback(
    async (docId: string) => {
      if (reviewRequested.current.has(docId)) return;
      reviewRequested.current.add(docId);
      try {
        patchReview(docId, { loaded: true, review: await API.getActivityDocReview(projectId, docId) });
      } catch (err) {
        patchReview(docId, { loaded: true, error: err });
      }
    },
    [patchReview, projectId],
  );

  const startReview = useCallback(
    async (docId: string) => {
      if (reviewing) return;
      setReviewing(docId);
      patchReview(docId, { running: true, error: null });
      try {
        patchReview(docId, { loaded: true, review: await API.reviewActivityDoc(projectId, docId), running: false });
        void reload();
      } catch (err) {
        patchReview(docId, { running: false, error: err });
      } finally {
        setReviewing(null);
      }
    },
    [patchReview, projectId, reload, reviewing],
  );

  const run = useCallback(
    async (target: Target, input: { instructions?: string; confirmSkip?: boolean }) => {
      if (busy) return;
      setBusy(target.key);
      setPanelOpen(true);
      setPanel({ kind: "generating", target });
      try {
        const doc = await API.generateActivityDoc(projectId, target.key, input);
        setPanel({ kind: "doc", target, doc, notice: null });
        void reload();
      } catch (err) {
        const skipped = API.skippedActivities(err);
        if (skipped) {
          setPanel(null);
          setSkip({ target, instructions: input.instructions, ...skipped });
        } else {
          setPanel({ kind: "error", target, error: err, retry: () => void run(target, { ...input, confirmSkip: true }) });
        }
      } finally {
        setBusy(null);
      }
    },
    [busy, projectId, reload, setPanelOpen],
  );

  const open = useCallback(
    async (target: Target, docId: string, tab: PanelTab = "doc") => {
      setPanelOpen(true);
      setPanel({ kind: "loading", target, tab });
      try {
        const doc = await API.getActivityDoc(projectId, docId);
        // A row's review may have switched tabs while the document loaded.
        setPanel((p) => ({ kind: "doc", target, doc, notice: null, tab: p?.kind === "loading" && p.tab ? p.tab : tab }));
      } catch (err) {
        setPanel({ kind: "error", target, error: err, retry: () => void open(target, docId, tab) });
      }
    },
    [projectId, setPanelOpen],
  );

  /** The panel on a saved version's Hasil reviu: switched to when it is shown already, opened otherwise. */
  const showReviewTab = useCallback(
    (target: Target, docId: string) => {
      if (panel?.kind === "doc" && panel.doc.id === docId) {
        setPanelOpen(true);
        setPanel({ ...panel, tab: "review" });
      } else void open(target, docId, "review");
    },
    [open, panel, setPanelOpen],
  );

  // A row's upload: the new version shows in the panel like a fresh draft,
  // unless a draft is being written there (that would hide its progress).
  const upload = useCallback(
    async (target: Target, file: File) => {
      const saved = await API.uploadActivityDoc(projectId, target.key, file);
      void reload();
      if (!busy) {
        setPanelOpen(true);
        setPanel({ kind: "doc", target, doc: saved, notice: uploadNotice(saved) });
      }
      return saved;
    },
    [busy, projectId, reload, setPanelOpen],
  );

  const api = useMemo<ActivityDocsApi>(
    () => ({
      projectId,
      state: (key) => states.get(key),
      busy,
      reviewing,
      generate: (target, instructions) => void run(target, instructions ? { instructions } : {}),
      askInstructions: (target) => setAsking({ target }),
      open: (target, docId) => void open(target, docId),
      upload,
      review: (target, docId) => {
        showReviewTab(target, docId);
        void startReview(docId);
      },
      showReview: showReviewTab,
    }),
    [projectId, states, busy, reviewing, run, open, upload, showReviewTab, startReview],
  );

  const panelDocId = panel?.kind === "doc" ? panel.doc.id : null;
  const view = panel ? (
    <ActivityDocView
      projectId={projectId}
      inSheet={!wide}
      panel={panel}
      onPanel={setPanel}
      onSaved={() => void reload()}
      onRegenerate={(withInstructions) => (withInstructions ? setAsking({ target: panel.target }) : void run(panel.target, { confirmSkip: true }))}
      onClose={() => setPanel(null)}
      busy={busy !== null}
      review={panelDocId ? (reviews.get(panelDocId) ?? EMPTY_SLOT) : EMPTY_SLOT}
      reviewing={reviewing}
      onLoadReview={() => (panelDocId ? void loadReview(panelDocId) : undefined)}
      onReview={() => (panelDocId ? void startReview(panelDocId) : undefined)}
      onFix={(finding) => setAsking({ target: panel.target, prefill: fixInstruction(finding) })}
    />
  ) : null;

  return (
    <Ctx.Provider value={api}>
      <PanelSlotCtx.Provider value={wide ? view : null}>{children}</PanelSlotCtx.Provider>
      <span ref={probe} hidden />
      {!wide && panel ? (
        <Dialog open title={panel.target.title} onClose={() => setPanel(null)} size="lg" busy={panel.kind === "generating"}>
          {view}
        </Dialog>
      ) : null}
      <InstructionDialog
        target={asking?.target ?? null}
        prefill={asking?.prefill}
        onClose={() => setAsking(null)}
        onSubmit={(text) => {
          const target = asking?.target;
          setAsking(null);
          if (target) void run(target, { instructions: text, ...(panel?.target.key === target.key ? { confirmSkip: true } : {}) });
        }}
      />
      <SkipDialog
        state={skip}
        onClose={() => setSkip(null)}
        onConfirm={() => {
          const s = skip;
          setSkip(null);
          if (s) void run(s.target, { ...(s.instructions ? { instructions: s.instructions } : {}), confirmSkip: true });
        }}
      />
    </Ctx.Provider>
  );
}

const PanelSlotCtx = createContext<ReactNode>(null);

/**
 * The right panel's content: the open activity document when there is one
 * (on a wide frame), otherwise the panel's own content.
 */
export function ActivityDocPanelSlot({ children }: { children: ReactNode }) {
  const view = useContext(PanelSlotCtx);
  return <>{view ?? children}</>;
}

/** Buat dokumen and Buat dengan arahan for one activity or template, and its saved version or draft. */
export function ActivityDocButtons({ activityKey, title, variant = "row" }: { activityKey: string; title: string; variant?: "row" | "card" }) {
  const docs = useActivityDocs();
  if (!docs) return null;
  const target = { key: activityKey, title };
  const running = docs.busy === activityKey;
  const cls = variant === "row" ? "btn btn-secondary pk-ask" : "btn btn-secondary";
  return (
    <>
      <button
        type="button"
        className={cls}
        disabled={docs.busy !== null}
        aria-busy={running || undefined}
        title={`Buat dokumen: ${title}`}
        aria-label={`Buat dokumen ${title}`}
        onClick={() => docs.generate(target)}
      >
        <Glyph name="docAdd" size={18} />
        <span className="pk-ask-text">{running ? "Pluto menyusun" : "Buat dokumen"}</span>
      </button>
      <button
        type="button"
        className={cls}
        disabled={docs.busy !== null}
        aria-haspopup="dialog"
        title={`Buat dokumen dengan arahan: ${title}`}
        aria-label={`Buat dokumen ${title} dengan arahan`}
        onClick={() => docs.askInstructions(target)}
      >
        <Glyph name="magicStar" size={18} />
        <span className="pk-ask-text">Buat dengan arahan</span>
      </button>
      <ActivityDocReviewButton activityKey={activityKey} title={title} className={cls} />
    </>
  );
}

/** Reviu dokumen for an activity with a saved version (its newest one); nothing without one. */
export function ActivityDocReviewButton({ activityKey, title, className = "btn btn-secondary pk-ask" }: { activityKey: string; title: string; className?: string }) {
  const docs = useActivityDocs();
  const saved = docs?.state(activityKey)?.saved;
  if (!docs || !saved) return null;
  const running = docs.reviewing === saved.id;
  return (
    <button
      type="button"
      className={className}
      disabled={docs.busy !== null || docs.reviewing !== null}
      aria-busy={running || undefined}
      title={`Review dokumen: ${title} (versi ${saved.version})`}
      aria-label={`Review dokumen ${title}`}
      onClick={() => docs.review({ key: activityKey, title }, saved.id)}
    >
      <Glyph name="shieldTick" size={18} />
      <span className="pk-ask-text">{running ? "Pluto mereviu" : "Review dokumen"}</span>
    </button>
  );
}

/**
 * Unggah dokumen on an activity's row: a Word, PDF or Excel file (up to 10
 * MB) becomes its next saved version without opening the panel first. The
 * button reads Mengunggah meanwhile; a problem goes to `onError` for the
 * row's own error line (null clears it).
 */
export function ActivityDocUpload({ activityKey, title, onError }: { activityKey: string; title: string; onError: (message: string | null) => void }) {
  const docs = useActivityDocs();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  if (!docs) return null;
  const send = async (file: File) => {
    const problem = checkDocUpload(file);
    onError(problem);
    if (problem) return;
    setBusy(true);
    try {
      await docs.upload({ key: activityKey, title }, file);
    } catch (err) {
      onError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button
        type="button"
        className="btn btn-secondary pk-ask"
        disabled={busy || docs.busy === activityKey}
        aria-busy={busy || undefined}
        title={`Unggah dokumen: ${title} (Word, PDF, atau Excel, maks. 10 MB)`}
        aria-label={`Unggah dokumen ${title}`}
        onClick={() => input.current?.click()}
      >
        <Glyph name="paperclip" size={18} />
        <span className="pk-ask-text">{busy ? "Mengunggah" : "Unggah"}</span>
      </button>
      <input
        ref={input}
        type="file"
        hidden
        accept={DOC_UPLOAD_ACCEPT}
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          e.currentTarget.value = "";
          if (file) void send(file);
        }}
      />
    </>
  );
}

/** The activity's saved version or open draft, as a line that reopens it in the panel. */
export function ActivityDocStatus({ activityKey, title }: { activityKey: string; title: string }) {
  const docs = useActivityDocs();
  const s = docs?.state(activityKey);
  if (!docs || !s || (!s.saved && !s.draft)) return null;
  const target = { key: activityKey, title };
  return (
    <span className="ad-status">
      {s.saved ? (
        <button type="button" className="ad-status-link" onClick={() => docs.open(target, s.saved!.id)}>
          <Glyph name="tickCircle" size={16} />
          <span>
            Tersimpan versi {s.saved.version}
            {s.saved.source === "upload" ? " (hasil edit)" : ""}
          </span>
        </button>
      ) : null}
      {s.saved?.reviewStatus ? (
        <button
          type="button"
          className="ad-review-chip"
          title={s.saved.reviewedAt ? `Direviu ${formatDateTime(s.saved.reviewedAt)}` : undefined}
          aria-label={`Lihat hasil reviu ${title}: ${REVIEW_STATUS_LABEL[s.saved.reviewStatus]}`}
          onClick={() => docs.showReview(target, s.saved!.id)}
        >
          <Badge tone={REVIEW_STATUS_TONE[s.saved.reviewStatus]}>Direviu: {REVIEW_STATUS_LABEL[s.saved.reviewStatus]}</Badge>
        </button>
      ) : null}
      {s.draft ? (
        <button type="button" className="ad-status-link" data-draft="" onClick={() => docs.open(target, s.draft!.id)}>
          <Glyph name="documentText" size={16} />
          <span>Draf belum disimpan</span>
        </button>
      ) : null}
    </span>
  );
}

/** The panel's line after an upload. */
function uploadNotice(saved: ActivityDoc): string {
  return saved.fileStored
    ? `Hasil edit tersimpan sebagai versi ${saved.version} dan menjadi acuan aktivitas berikutnya.`
    : `Isi hasil edit tersimpan sebagai versi ${saved.version}. File aslinya belum tersimpan di Drive, tetapi isinya tetap menjadi acuan.`;
}

function sourceLabel(doc: ActivityDoc): string {
  if (doc.status === "draft") return "Draf belum disimpan";
  return doc.source === "upload" ? `Versi ${doc.version}, hasil edit diunggah` : `Versi ${doc.version}, tersimpan`;
}

interface ViewProps {
  projectId: string;
  /** Shown in a dialog (narrow frame): the dialog has its own close button. */
  inSheet: boolean;
  panel: PanelState;
  busy: boolean;
  onPanel: (next: PanelState) => void;
  onSaved: () => void;
  onRegenerate: (withInstructions: boolean) => void;
  onClose: () => void;
  /** The shown saved version's review. */
  review: ReviewSlot;
  /** The saved version being reviewed (any), one at a time. */
  reviewing: string | null;
  onLoadReview: () => void;
  onReview: () => void;
  onFix: (finding: ReviewFinding) => void;
}

type DocumentBlock = NonNullable<ActivityDoc["document"]>["blocks"][number];

/** A section of the shown document: its heading's block index (-1 for what precedes the first heading) and its blocks. */
export interface DocSection {
  at: number;
  blocks: DocumentBlock[];
}

/** The document cut at its top-level headings; a section keeps its subheadings. */
export function docSections(blocks: DocumentBlock[]): DocSection[] {
  const levels = blocks.flatMap((b) => (b.type === "heading" ? [b.level] : []));
  const top = levels.length > 0 ? Math.min(...levels) : 0;
  const out: DocSection[] = [];
  blocks.forEach((b, i) => {
    if (b.type === "heading" && b.level === top) out.push({ at: i, blocks: [b] });
    else if (out.length === 0) out.push({ at: -1, blocks: [b] });
    else out[out.length - 1]!.blocks.push(b);
  });
  return out;
}

/** A section body in the editor's shape; the API's parseSectionText reads it back. */
export function sectionText(blocks: DocumentBlock[]): string {
  return blocks
    .map((b) => {
      switch (b.type) {
        case "heading":
          return `${b.level >= 3 ? "###" : "##"} ${b.text}`;
        case "paragraph":
          return b.text;
        case "list":
          return b.items.map((it, i) => (b.ordered ? `${i + 1}. ${it}` : `- ${it}`)).join("\n");
        case "table":
          return [b.headers, ...b.rows].filter((r) => r.length > 0).map((r) => r.join(" | ")).join("\n");
      }
    })
    .join("\n\n");
}

export const SECTION_EDIT_HINT = "Pisahkan paragraf dengan baris kosong. Daftar: awali baris dengan \"- \". Tabel: pisahkan kolom dengan \" | \", baris pertama judul kolom.";
export const SECTION_AI_HINT = "Hanya bagian ini yang ditulis ulang. Bagian lain dokumen dipakai Pluto sebagai acuan agar isinya tetap selaras.";

type SectionEdit = { at: number; mode: "manual" | "ai"; text: string };

/**
 * The panel's document, each top-level section with Edit (manual) and AI
 * (rewrite that section only). Either result is the user's new draft.
 */
function SectionedDocument({ projectId, doc, disabled, onUpdated }: { projectId: string; doc: ActivityDoc; disabled: boolean; onUpdated: (doc: ActivityDoc, title: string) => void }) {
  const [edit, setEdit] = useState<SectionEdit | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seconds = useElapsed(running && edit?.mode === "ai");
  const fieldId = useId();
  const blocks = doc.document?.blocks ?? [];
  const sections = useMemo(() => docSections(blocks), [blocks]);

  useEffect(() => {
    setEdit(null);
    setError(null);
  }, [doc.id]);

  const open = (s: DocSection, mode: SectionEdit["mode"]) => {
    setError(null);
    setEdit({ at: s.at, mode, text: mode === "manual" ? sectionText(s.blocks.slice(1)) : "" });
  };
  const submit = async (title: string) => {
    if (!edit || running) return;
    setRunning(true);
    setError(null);
    try {
      const updated =
        edit.mode === "manual"
          ? await API.editActivityDocSection(projectId, doc.id, edit.at, edit.text)
          : await API.rewriteActivityDocSection(projectId, doc.id, edit.at, edit.text.trim() || undefined);
      setEdit(null);
      onUpdated(updated, title);
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      {sections.map((s) => {
        const heading = s.blocks[0]!;
        const title = heading.type === "heading" ? heading.text : "";
        const editing = edit !== null && edit.at === s.at;
        return (
          <section key={s.at} className={`ad-section${editing ? " is-editing" : ""}`} aria-label={s.at >= 0 ? title : undefined}>
            {s.at >= 0 ? (
              // The heading row: the section's own tools sit beside its heading, so they
              // read as "this section" and not as the whole document's actions above.
              <div className="ad-section-head">
                <DocumentBlocks blocks={[heading]} />
                {!editing ? (
                  <div className="ad-section-tools">
                    <button type="button" className="btn btn-ghost btn-sm" disabled={disabled || running || edit !== null} aria-label={`Edit bagian ${title}`} onClick={() => open(s, "manual")}>
                      <Glyph name="documentText" size={16} />
                      <span>Edit bagian ini</span>
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" disabled={disabled || running || edit !== null} aria-label={`Tulis ulang bagian ${title} dengan Pluto`} onClick={() => open(s, "ai")}>
                      <Glyph name="magicStar" size={16} />
                      <span>Tulis ulang bagian ini</span>
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
            {editing && edit.mode === "manual" ? null : <DocumentBlocks blocks={s.at >= 0 ? s.blocks.slice(1) : s.blocks} />}
            {editing ? (
              <div className="ad-section-editor">
                <Field id={fieldId} label={edit.mode === "manual" ? "Isi bagian" : "Arahan untuk bagian ini (opsional)"} hint={edit.mode === "manual" ? SECTION_EDIT_HINT : SECTION_AI_HINT}>
                  <textarea
                    id={fieldId}
                    data-autofocus=""
                    rows={edit.mode === "manual" ? 12 : 3}
                    value={edit.text}
                    disabled={running}
                    maxLength={edit.mode === "manual" ? MAX_SECTION_TEXT : MAX_INSTRUCTIONS}
                    placeholder={edit.mode === "ai" ? "Contoh: tambahkan tabel spesifikasi minimum dan alasan pemilihannya" : undefined}
                    aria-describedby={`${fieldId}-hint`}
                    onChange={(e) => setEdit({ ...edit, text: e.currentTarget.value })}
                  />
                </Field>
                {running && edit.mode === "ai" ? (
                  <p className="ad-panel-hint" role="status">
                    Pluto menulis ulang bagian ini ({seconds} detik)
                  </p>
                ) : null}
                {error ? (
                  <p className="pk-item-error" role="alert">
                    <Glyph name="danger" size={16} />
                    <span>{error}</span>
                  </p>
                ) : null}
                <div className="ad-panel-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={running || (edit.mode === "manual" && edit.text.trim().length === 0)}
                    aria-busy={running || undefined}
                    onClick={() => void submit(title)}
                  >
                    <Glyph name={edit.mode === "manual" ? "tickCircle" : "magicStar"} size={18} />
                    <span>{running ? (edit.mode === "manual" ? "Menyimpan" : "Menulis ulang") : edit.mode === "manual" ? "Simpan bagian" : "Tulis ulang dengan Pluto"}</span>
                  </button>
                  <button type="button" className="btn btn-secondary" disabled={running} onClick={() => setEdit(null)}>
                    <span>Batal</span>
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        );
      })}
    </>
  );
}

function ActivityDocView({ projectId, inSheet, panel, busy, onPanel, onSaved, onRegenerate, onClose, review, reviewing, onLoadReview, onReview, onFix }: ViewProps) {
  const seconds = useElapsed(panel.kind === "generating");
  const fileInput = useRef<HTMLInputElement>(null);
  const [action, setAction] = useState<"save" | "download" | "upload" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const headingId = useId();
  const tabsId = useId();
  const { target } = panel;
  const reviewable = panel.kind === "doc" && panel.doc.status === "saved";
  const tab: PanelTab = reviewable && panel.tab === "review" ? "review" : "doc";
  const setTab = (next: PanelTab) => {
    if (panel.kind === "doc") onPanel({ ...panel, tab: next });
  };
  const startReview = () => {
    setTab("review");
    onReview();
  };

  // The results tab reads the latest review once.
  useEffect(() => {
    if (tab === "review" && !review.loaded && !review.running) onLoadReview();
  }, [tab, review.loaded, review.running, onLoadReview]);

  useEffect(() => setError(null), [panel.kind, panel.kind === "doc" ? panel.doc.id : null]);

  const act = async (kind: "save" | "download" | "upload", work: () => Promise<void>) => {
    if (action) return;
    setAction(kind);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setAction(null);
    }
  };

  const doc = panel.kind === "doc" ? panel.doc : null;
  const save = () =>
    act("save", async () => {
      if (!doc) return;
      const saved = await API.saveActivityDoc(projectId, doc.id);
      onPanel({
        kind: "doc",
        target,
        doc: saved,
        notice: saved.fileStored
          ? `Tersimpan sebagai versi ${saved.version}, dan file Word-nya disimpan di Drive. Dokumen ini menjadi acuan aktivitas berikutnya.`
          : `Tersimpan sebagai versi ${saved.version} dan menjadi acuan aktivitas berikutnya. File Word-nya belum tersimpan di Drive; klik Simpan file ke Drive untuk mencoba lagi.`,
      });
      onSaved();
    });
  const download = () =>
    act("download", async () => {
      if (doc) saveFile(await API.downloadActivityDoc(projectId, doc.id));
    });
  const upload = (file: File) =>
    act("upload", async () => {
      const saved = await API.uploadActivityDoc(projectId, target.key, file);
      onPanel({ kind: "doc", target, doc: saved, notice: uploadNotice(saved) });
      onSaved();
    });

  return (
    <section className="ad-panel" aria-labelledby={headingId}>
      <div className="ad-panel-head">
        {inSheet ? null : (
          <button type="button" className="pk-back-btn" onClick={onClose} disabled={panel.kind === "generating"}>
            <Glyph name="arrowLeft" size={16} />
            <span>Kembali ke panel project</span>
          </button>
        )}
        <h2 id={headingId} className="ad-panel-title">
          {doc?.title || target.title}
        </h2>
        {doc ? (
          <p className="ad-panel-meta">
            <Badge tone={doc.status === "draft" ? "warning" : "success"}>{sourceLabel(doc)}</Badge>
            {doc.status === "draft" ? <span>Belum menjadi acuan sampai disimpan.</span> : null}
          </p>
        ) : null}
        {doc && doc.status === "draft" ? (
          <p className="ad-panel-ref">
            {doc.reference ? (
              <>
                Acuan: <strong>{doc.reference.title}</strong> versi {doc.reference.version}
              </>
            ) : (
              "Tanpa acuan: belum ada dokumen tersimpan dari aktivitas sebelumnya."
            )}
          </p>
        ) : null}
        {doc && doc.status === "draft" && doc.chain.length > 0 ? (
          <p className="ad-panel-ref">
            Disusun dari {doc.chain.length} dokumen tersimpan sebelumnya di project ini: {doc.chain.map((c) => `${c.title} (versi ${c.version})`).join("; ")}
          </p>
        ) : null}
        {doc?.instructions ? <p className="ad-panel-ref">Arahan: {doc.instructions}</p> : null}
      </div>

      {panel.kind === "generating" ? <GenerationProgress what={target.title} seconds={seconds} /> : null}
      {panel.kind === "loading" ? (
        <p className="pj-empty" role="status">
          Memuat dokumen
        </p>
      ) : null}
      {panel.kind === "error" ? <GenerationError error={panel.error} onRetry={panel.retry} /> : null}

      {doc && reviewable ? (
        <Tabs
          label={`Tampilan ${doc.title}`}
          idBase={tabsId}
          value={tab}
          onChange={(id) => setTab(id === "review" ? "review" : "doc")}
          items={[
            { id: "doc", label: "Dokumen" },
            { id: "review", label: "Hasil review" },
          ]}
        />
      ) : null}

      {doc && tab === "review" ? (
        <div id={`${tabsId}-panel`} role="tabpanel" aria-labelledby={`${tabsId}-tab-review`} className="ad-review">
          <ReviewResults slot={review} version={doc.version} running={reviewing === doc.id} locked={reviewing !== null && reviewing !== doc.id} busy={busy} onReview={onReview} onFix={onFix} />
        </div>
      ) : null}

      {doc && tab === "doc" ? (
        <div className="ad-tab" {...(reviewable ? { id: `${tabsId}-panel`, role: "tabpanel", "aria-labelledby": `${tabsId}-tab-doc` } : {})}>
          {panel.kind === "doc" && panel.notice ? (
            <Notice tone="success" title={panel.noticeTitle ?? "Tersimpan"}>
              <p>{panel.notice}</p>
            </Notice>
          ) : null}
          <div className="ad-panel-actions">
            {doc.status === "draft" || (doc.source === "ai" && !doc.fileStored) ? (
              <button type="button" className="btn btn-primary" disabled={action !== null} aria-busy={action === "save" || undefined} onClick={() => void save()}>
                <Glyph name="tickCircle" size={18} />
                <span>{action === "save" ? "Menyimpan" : doc.status === "draft" ? "Simpan ke database" : "Simpan file ke Drive"}</span>
              </button>
            ) : null}
            <button type="button" className="btn btn-secondary" disabled={action !== null} aria-busy={action === "download" || undefined} onClick={() => void download()}>
              <Glyph name="documentDownload" size={18} />
              <span>{action === "download" ? "Menyiapkan" : "Unduh .docx"}</span>
            </button>
            <button type="button" className="btn btn-secondary" disabled={action !== null} aria-busy={action === "upload" || undefined} onClick={() => fileInput.current?.click()}>
              <Glyph name="paperclip" size={18} />
              <span>{action === "upload" ? "Mengunggah" : "Unggah hasil edit"}</span>
            </button>
            <input
              ref={fileInput}
              type="file"
              hidden
              accept={DOC_UPLOAD_ACCEPT}
              onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                e.currentTarget.value = "";
                if (file) void upload(file);
              }}
            />
          </div>
          <div className="ad-panel-actions">
            <button type="button" className="btn btn-secondary" disabled={busy || action !== null} onClick={() => onRegenerate(false)}>
              <Glyph name="refresh" size={18} />
              <span>Buat ulang dokumen</span>
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy || action !== null} aria-haspopup="dialog" onClick={() => onRegenerate(true)}>
              <Glyph name="magicStar" size={18} />
              <span>Buat ulang dengan arahan</span>
            </button>
            {reviewable ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy || action !== null || reviewing !== null}
                aria-busy={reviewing === doc.id || undefined}
                onClick={startReview}
              >
                <Glyph name="shieldTick" size={18} />
                <span>{reviewing === doc.id ? "Pluto mereviu" : "Review dokumen"}</span>
              </button>
            ) : null}
          </div>
          <p className="ad-panel-hint">Ingin mengubah isinya? Unduh .docx, edit, lalu unggah hasil editnya dalam Word, PDF, atau Excel. Versi terakhir yang disimpan dipakai sebagai acuan dan digabung di akhir tahap.</p>
          {error ? (
            <p className="pk-item-error" role="alert">
              <Glyph name="danger" size={16} />
              <span>{error}</span>
            </p>
          ) : null}
          <article className="ad-doc" aria-label={`Isi ${doc.title}`}>
            {doc.document ? (
              <SectionedDocument
                projectId={projectId}
                doc={doc}
                disabled={busy || action !== null}
                onUpdated={(updated, title) =>
                  onPanel({
                    kind: "doc",
                    target,
                    doc: updated,
                    noticeTitle: "Bagian diperbarui",
                    notice: `Bagian "${title}" diperbarui sebagai draf. Klik Simpan ke database agar menjadi acuan aktivitas berikutnya.`,
                  })
                }
              />
            ) : (
              <p className="pj-empty">Dokumen ini belum memiliki isi.</p>
            )}
          </article>
        </div>
      ) : null}
    </section>
  );
}

/** Pluto's review is a check aid; the decision stays with the official. */
export const REVIEW_HINT = "Hasil reviu Pluto membantu pemeriksaan. Keputusan akhir tetap pada pejabat yang berwenang.";

function reviewErrorTitle(err: unknown): string {
  if (err instanceof API.ApiError && err.status === 429) return "Batas reviu tercapai";
  if (err instanceof API.ApiError && err.status === 502) return "Layanan Pluto sedang tidak tersedia";
  return "Reviu belum berhasil";
}

/** Hasil reviu: the latest review of the shown saved version, or the way to make one. */
function ReviewResults({
  slot,
  version,
  running,
  locked,
  busy,
  onReview,
  onFix,
}: {
  slot: ReviewSlot;
  version: number | null;
  running: boolean;
  /** Another document is being reviewed. */
  locked: boolean;
  /** A draft is being written. */
  busy: boolean;
  onReview: () => void;
  onFix: (finding: ReviewFinding) => void;
}) {
  const seconds = useElapsed(running);
  const { review } = slot;
  const reviewButton = (label: string, primary = false) => (
    <button type="button" className={primary ? "btn btn-primary" : "btn btn-secondary"} disabled={running || locked || busy} aria-busy={running || undefined} onClick={onReview}>
      <Glyph name={label === "Review ulang" ? "refresh" : "shieldTick"} size={18} />
      <span>{running ? "Pluto mereview" : label}</span>
    </button>
  );

  if (running) {
    return (
      <div className="gen-progress" role="status" aria-live="polite">
        <p className="gen-title">Pluto mereview dokumen versi {version}</p>
        <div className="ui-progress">
          <progress aria-label="Pluto mereview dokumen" />
        </div>
        <p className="gen-meta">
          <span className="num">{seconds}</span> detik berjalan. {seconds >= 45 ? "Masih diproses, mohon tunggu sebentar lagi." : "Biasanya 15 sampai 60 detik. Jangan tutup halaman ini."}
        </p>
      </div>
    );
  }

  const failure = slot.error ? (
    <Notice tone={slot.error instanceof API.ApiError && slot.error.status === 429 ? "warning" : "danger"} title={reviewErrorTitle(slot.error)} action={<div className="button-row">{reviewButton("Coba lagi")}</div>}>
      <p>{API.errorMessage(slot.error)}</p>
    </Notice>
  ) : null;

  if (!slot.loaded) {
    return (
      <>
        {failure}
        <p className="pj-empty" role="status">
          Memuat hasil reviu
        </p>
      </>
    );
  }

  if (!review) {
    return (
      <>
        {failure}
        <EmptyState
          icon="shieldTick"
          title="Dokumen ini belum direview"
          body={`Pluto memeriksa kelengkapan, konsistensi dengan dokumen sebelumnya, dan kesesuaian aturan pada versi ${version}.`}
          action={reviewButton("Review dokumen", true)}
        />
      </>
    );
  }

  const groups = REVIEW_CATEGORIES.map((c) => ({
    ...c,
    findings: review.findings.filter((f) => f.kategori === c.id).sort((a, b) => LEVEL_ORDER.indexOf(a.tingkat) - LEVEL_ORDER.indexOf(b.tingkat)),
  })).filter((g) => g.findings.length > 0);

  return (
    <>
      {failure}
      <div className="ad-review-head">
        <p className="ad-panel-meta">
          <Badge tone={REVIEW_STATUS_TONE[review.status]}>{REVIEW_STATUS_LABEL[review.status]}</Badge>
          <span>
            Versi {version}, direviu {formatDateTime(review.createdAt)}
          </span>
        </p>
        <p className="ad-review-summary">{review.summary}</p>
        <div className="ad-panel-actions">{reviewButton("Review ulang")}</div>
      </div>
      {groups.length === 0 ? (
        <EmptyState icon="tickCircle" title="Tidak ada temuan" body="Pluto tidak menemukan hal yang perlu diperbaiki pada versi ini." />
      ) : (
        groups.map((g) => (
          <section key={g.id} className="ad-review-group" aria-label={g.label}>
            <h3 className="ad-review-group-title">
              {g.label} <span className="ad-review-count">({g.findings.length})</span>
            </h3>
            <ul className="ad-findings">
              {g.findings.map((f, i) => (
                <li key={`${g.id}-${i}`} className="ad-finding">
                  <p className="ad-finding-meta">
                    <Badge tone={LEVEL_TONE[f.tingkat]}>{LEVEL_LABEL[f.tingkat]}</Badge>
                    <span className="ad-finding-section">{f.bagian ? `Bagian: ${f.bagian}` : "Seluruh dokumen"}</span>
                  </p>
                  <p className="ad-finding-text">{f.temuan}</p>
                  {f.saran ? (
                    <p className="ad-finding-detail">
                      <strong>Saran:</strong> {f.saran}
                    </p>
                  ) : null}
                  {f.rujukan ? (
                    <p className="ad-finding-detail">
                      <strong>Rujukan:</strong> {f.rujukan}
                    </p>
                  ) : null}
                  <div className="ad-finding-actions">
                    <button
                      type="button"
                      className="btn btn-secondary ad-finding-fix"
                      disabled={busy}
                      aria-haspopup="dialog"
                      aria-label={`Perbaiki dengan arahan: ${f.temuan}`}
                      onClick={() => onFix(f)}
                    >
                      <Glyph name="magicStar" size={18} />
                      <span>Perbaiki dengan arahan</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
      <p className="ad-panel-hint">{REVIEW_HINT}</p>
    </>
  );
}

function InstructionDialog({ target, prefill, onClose, onSubmit }: { target: Target | null; prefill?: string; onClose: () => void; onSubmit: (text: string) => void }) {
  const id = useId();
  const [text, setText] = useState("");
  useEffect(() => {
    if (target) setText(prefill ?? "");
  }, [target, prefill]);
  const clean = text.trim();
  return (
    <Dialog
      open={target !== null}
      title="Buat dokumen dengan arahan"
      description={target ? target.title : undefined}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Batal
          </button>
          <button type="button" className="btn btn-primary" disabled={clean.length === 0} onClick={() => onSubmit(clean)}>
            <Glyph name="docAdd" size={18} />
            <span>Buat dokumen</span>
          </button>
        </>
      }
    >
      <Field id={`${id}-text`} label="Arahan untuk Pluto" hint={`Mis. bagian yang perlu ditambahkan, gaya bahasa, atau data yang harus dipakai. ${clean.length} dari ${MAX_INSTRUCTIONS} karakter.`}>
        <textarea
          id={`${id}-text`}
          data-autofocus=""
          rows={5}
          maxLength={MAX_INSTRUCTIONS}
          value={text}
          aria-describedby={`${id}-text-hint`}
          onChange={(e) => setText(e.currentTarget.value)}
        />
      </Field>
    </Dialog>
  );
}

function SkipDialog({ state, onClose, onConfirm }: { state: { target: Target; skipped: SkippedActivity[]; total: number } | null; onClose: () => void; onConfirm: () => void }) {
  const more = state ? state.total - state.skipped.length : 0;
  return (
    <Dialog
      open={state !== null}
      title="Aktivitas sebelumnya belum selesai"
      size="sm"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Batal
          </button>
          <button type="button" className="btn btn-primary" onClick={onConfirm}>
            Tetap buat dokumen
          </button>
        </>
      }
    >
      <p className="dialog-text">
        Aktivitas berikut belum dicentang dan belum punya dokumen tersimpan. Dokumen {state?.target.title} tetap dapat dibuat, tetapi hanya memakai dokumen tersimpan yang
        sudah ada sebagai acuan.
      </p>
      <ul className="ad-skip-list">
        {(state?.skipped ?? []).map((s) => (
          <li key={s.key}>
            <span className="ad-skip-stage">{s.stageLabel}</span>
            <span>{s.title}</span>
          </li>
        ))}
      </ul>
      {more > 0 ? <p className="dialog-text">dan {more} aktivitas lainnya.</p> : null}
      <p className="dialog-text">Urutan ini disarankan agar hasilnya lebih akurat, tetapi tidak wajib.</p>
    </Dialog>
  );
}
